import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, Transition as _Transition } from "vue"
import _imports_0 from '@/assets/edit-plus-active.svg'
import _imports_1 from '@/assets/pencil-gray.svg'


const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center"
}
const _hoisted_3 = { class: "ml-[8px] text-[20px] font-semibold text-[#393840]" }
const _hoisted_4 = {
  key: 1,
  class: "flex items-center"
}
const _hoisted_5 = { class: "ml-[8px] text-[20px] font-semibold text-[#393840]" }

import { errorToast, successToast } from "@/utils/custom-toast";
import { computed, ref } from "vue";
import client from "@/utils/axios";
import BonusReportForm from "./BonusReportForm.vue";
import BonusReportReview from "./BonusReportReview.vue";
import BonusReportSuccess from "./BonusReportSuccess.vue";
import { useTranslate } from "@/composables/useTranslate";
import { WorkerBonus } from "@/types";

type Props = {
  isReportDialogOpen: boolean;
  bonusDate: any;
  isEdit: boolean;
  month: string | null;
  year: number | null;
};

type Emits = {
  (event: "onClose"): void;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'BonusReportDialog',
  props: {
    isReportDialogOpen: { type: Boolean },
    bonusDate: {},
    isEdit: { type: Boolean },
    month: {},
    year: {}
  },
  emits: ["onClose"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const currentDialogStage = ref(1);

const emits = __emit;
const isModalOpen = computed(() => props.isReportDialogOpen);
const isModalEdit = computed(() => props.isEdit);

const selectedUrl = ref();
const { t } = useTranslate();
const isLoading = ref(false);

// GET MONTHS
let months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item: any) => {
  return {
    id: item,
    name: t(`months.${item}`),
  };
});

const workersBonuses = ref<Array<WorkerBonus>>([]);

const currentMonthIndex = new Date().getMonth();

const selectedMonth = ref(
  props.month
    ? months.find((m) => m.id === props.month) || months[currentMonthIndex]
    : months[currentMonthIndex]
);
const areBonusesValid = computed(() => {
  if (workersBonuses.value) {
    let isValid = true;
    workersBonuses.value.forEach((workerBonus: WorkerBonus) => {
      if (
        !workerBonus.bonusPayoutTotal.valid ||
        !workerBonus.commercialTotal.valid ||
        !workerBonus.scaffoldingTotal.valid ||
        !workerBonus.bonusTotal.valid
      ) {
        isValid = false;
      }
    });
    return isValid;
  }
  return false;
});
// GET YEARS
const currentYear = new Date().getFullYear();
const rawYears = Array.from({ length: 3 }, (_, i) => currentYear + i);

const years = rawYears.map((year) => ({ id: year, name: year.toString() }));

const selectedYear = ref(
  props.year
    ? years.find((y) => y.id === props.year) || years[0]
    : years.find((y) => y.id === currentYear) || years[0]
);

const handleClose = () => {
  currentDialogStage.value = 1;
  selectedYear.value = years.find((y) => y.id === currentYear) || years[0];
  selectedMonth.value = months[currentMonthIndex];
  selectedUrl.value = "";
  emits("onClose");
};

const handleSuccessClose = () => {
  if (props.isEdit) {
    successToast(
      `Successfully updated bonus report for ${props.bonusDate.month}/${props.bonusDate.year}`
    );
  } else {
    successToast(
      `Successfully added bonus report for ${selectedMonth.value.name}/${selectedYear.value.name}`
    );
  }
  currentDialogStage.value = 1;
  selectedYear.value = years.find((y) => y.id === currentYear) || years[0];
  selectedMonth.value = months[currentMonthIndex];
  selectedUrl.value = "";
  emits("onClose");
};

const increaseDialogStage = () => {
  currentDialogStage.value = currentDialogStage.value + 1;
};

const decreaseDialogStage = () => {
  currentDialogStage.value = currentDialogStage.value - 1;
};

const submitReport = async () => {
  try {
    await client.post("/installers/monthly-bonus/report", {
      month: selectedMonth.value?.id,
      year: selectedYear.value.id,
      sheetUrl: selectedUrl.value,
      workersBonuses: workersBonuses.value,
    });

    increaseDialogStage();
  } catch (error: any) {
    errorToast(error);
    console.log(error);
  }
};

const onUploadBonusSheet = async (month: any, year: any, url: string) => {
  selectedMonth.value = month;
  selectedYear.value = year;
  selectedUrl.value = url;
  isLoading.value = true;
  try {
    const response = await client.post("/installers/monthly-bonus", {
      sheetUrl: selectedUrl.value,
      month: selectedMonth.value?.id,
      year: selectedYear.value?.id,
      isUpdate: props.isEdit,
    });
    isLoading.value = false;
    if (response.status === 200) {
      workersBonuses.value = response.data;

      increaseDialogStage();
    }

    isLoading.value = false;
  } catch (error: any) {
    console.log(error);
    isLoading.value = false;
    if (error.status === 400) {
      errorToast(t(`installers.${error.response?.data}`).value);
    } else {
      errorToast(t(`global.something_went_wrong`).value);
    }
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "nested" }, {
    default: _withCtx(() => [
      (isModalOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (currentDialogStage.value === 1)
              ? (_openBlock(), _createBlock(BonusReportForm, {
                  key: 0,
                  "selected-month": selectedMonth.value,
                  "selected-year": selectedYear.value,
                  "selected-url": selectedUrl.value,
                  months: _unref(months),
                  years: _unref(years),
                  "is-report-dialog-open": isModalOpen.value,
                  "is-edit": __props.isEdit,
                  "is-loading": isLoading.value,
                  onOnClose: _cache[0] || (_cache[0] = ($event: any) => (handleClose())),
                  onIncreaseDialogStage: _cache[1] || (_cache[1] = ($event: any) => (increaseDialogStage())),
                  onOnSubmitBonus: onUploadBonusSheet
                }, {
                  "custom-header": _withCtx(() => [
                    (!isModalEdit.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                          _cache[5] || (_cache[5] = _createElementVNode("img", {
                            class: "w-[18px] h-[18px]",
                            src: _imports_0,
                            alt: ""
                          }, null, -1)),
                          _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("installers.new_bonus_report")), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _cache[6] || (_cache[6] = _createElementVNode("img", {
                            class: "w-[18px] h-[18px]",
                            src: _imports_1,
                            alt: ""
                          }, null, -1)),
                          _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("global.edit")) + " " + _toDisplayString(selectedMonth.value.name + "/" + selectedYear.value.name), 1)
                        ]))
                  ]),
                  _: 1
                }, 8, ["selected-month", "selected-year", "selected-url", "months", "years", "is-report-dialog-open", "is-edit", "is-loading"]))
              : _createCommentVNode("", true),
            (currentDialogStage.value === 2)
              ? (_openBlock(), _createBlock(BonusReportReview, {
                  key: 1,
                  "is-report-dialog-open": isModalOpen.value,
                  "workers-bonuses": workersBonuses.value,
                  "selected-month": selectedMonth.value,
                  "selected-year": selectedYear.value,
                  "are-bonuses-valid": areBonusesValid.value,
                  onOnClose: _cache[2] || (_cache[2] = ($event: any) => (handleClose())),
                  onDecreaseDialogStage: _cache[3] || (_cache[3] = ($event: any) => (decreaseDialogStage())),
                  onSubmitReport: submitReport
                }, null, 8, ["is-report-dialog-open", "workers-bonuses", "selected-month", "selected-year", "are-bonuses-valid"]))
              : _createCommentVNode("", true),
            (currentDialogStage.value === 3)
              ? (_openBlock(), _createBlock(BonusReportSuccess, {
                  key: 2,
                  "is-report-dialog-open": isModalOpen.value,
                  "bonus-report-date": selectedMonth.value.name + ' ' + selectedYear.value.name,
                  onOnClose: _cache[4] || (_cache[4] = ($event: any) => (handleSuccessClose()))
                }, null, 8, ["is-report-dialog-open", "bonus-report-date"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})